import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';

import {SequencePrimer} from 'BioJS';
import * as util from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {FindPrimersPreferences, RESULT_SORT_STRAND_TOGGLE, RESULT_SORTS} from 'constants/PrimerConstants';
import {
  FindPrimersOptions,
  RESULT_SORT_TO_DISPLAY_NAME,
  ResultSort,
} from 'molbio/find-primers/FindPrimersConstants';
import FindPrimersRow from 'molbio/find-primers/FindPrimersRow';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import usePreference from 'ui-library/hooks/usePreference';
import HelpIcon from 'ui-library/icons/HelpIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import sortedBy, {KeyAccessor} from 'util/sortedBy';
import {DEGREE_SIGN, MELTING_TEMP_ABBREVIATION} from 'util/UnicodeSymbols';

/**
 * The 'strand' option is unintuitive. It corresponds to two results sort preferences (sort by binding
 * location from 5' to 3' on the forward strand, sort by binding location from 5' to 3' on the reverse strand)
 * and toggles between the two sorts rather than showing both as separate options.
 * @param findType The type of primers search being done; i.e. when searching for binding locations for a
 *                 single primer we don't show its name in the results table.
 */
function getResultSortOptionsAndDisplayNames(findType: FindPrimersOptions): {
  [resultSort: ResultSort | typeof RESULT_SORT_STRAND_TOGGLE]: string;
} {
  if (findType === FindPrimersOptions.FOLDERS || findType === FindPrimersOptions.WORKLIST) {
    return {
      [RESULT_SORTS.LOCATION]: RESULT_SORT_TO_DISPLAY_NAME[RESULT_SORTS.LOCATION],
      [RESULT_SORTS.TEMPERATURE]: RESULT_SORT_TO_DISPLAY_NAME[RESULT_SORTS.TEMPERATURE],
      [RESULT_SORTS.NAME]: RESULT_SORT_TO_DISPLAY_NAME[RESULT_SORTS.NAME],
      [RESULT_SORT_STRAND_TOGGLE]: 'Strand',
      [RESULT_SORTS.PERCENT_MATCH]: RESULT_SORT_TO_DISPLAY_NAME[RESULT_SORTS.PERCENT_MATCH],
    };
  } else {
    return {
      [RESULT_SORTS.LOCATION]: RESULT_SORT_TO_DISPLAY_NAME[RESULT_SORTS.LOCATION],
      [RESULT_SORTS.TEMPERATURE]: RESULT_SORT_TO_DISPLAY_NAME[RESULT_SORTS.TEMPERATURE],
      [RESULT_SORT_STRAND_TOGGLE]: 'Strand',
      [RESULT_SORTS.PERCENT_MATCH]: RESULT_SORT_TO_DISPLAY_NAME[RESULT_SORTS.PERCENT_MATCH],
    };
  }
}

function strandCompHelper(strand: StrandOfDoubleStranded): (primer: SequencePrimer) => number {
  return (primer: SequencePrimer) => {
    const onSelectedStrand = primer.strand() === strand;
    if (onSelectedStrand) {
      return -1.0 / primer.end();
    } else {
      return primer.end();
    }
  };
}

const COMPARATORS: {
  [resultSort: ResultSort]: (sequence?: Sequence) => KeyAccessor<SequencePrimer>;
} = {
  location(sequence: Sequence) {
    return (primer: SequencePrimer) => primer.bindPosition(sequence);
  },
  temperature() {
    return (primer: SequencePrimer) =>
      -util.meltingTemperature(primer.bindingBases(), primer.nucleotideType());
  },
  name() {
    return {
      naturalSort: true,
      accessor: (primer: SequencePrimer) => primer.get('name'),
    };
  },
  // These are named such because these are the stringy value of the sorting preference.
  // strandPlus refers to a binding site on the forward strand of the sequence...
  strandPlus() {
    return (primer: SequencePrimer) => strandCompHelper(1)(primer);
  },
  // ...and strandMinus refers to a binding site on the reverse strand of the sequence.
  strandMinus() {
    return (primer: SequencePrimer) => strandCompHelper(-1)(primer);
  },
  percentMatch(sequence: Sequence) {
    return (primer: SequencePrimer) =>
      // @ts-expect-error This is a component sequence but mismatchedIndices expects a biojs sequence
      primer.mismatchedIndices(sequence).length / primer.bindingBases().length;
  },
};

function sortResults(
  results: Array<SequencePrimer>,
  resultSort: ResultSort,
  sequence: Sequence
): Array<SequencePrimer> {
  const comparator = COMPARATORS[resultSort](sequence);
  return sortedBy(results, [comparator]);
}

export interface FindPrimersResultsProps {
  /** Whether to highlight matching bases in the results table */
  highlightMatches: boolean;
  /** What sort of search we're doing, e.g. for all primers in a folder or for binding sites for one primer */
  findType: FindPrimersOptions;
  /** The search results to display */
  results: Array<SequencePrimer>;
  /** The sequence we're finding primers for */
  sequence: Sequence;
  /** What to do when the checkbox for a primer is selected */
  onSelectPrimer: () => void;
  /** What to do when the 'edit' link for a primer is selected */
  onClickEdit: (primer: SequencePrimer) => void;
}

export default function FindPrimersResults({
  highlightMatches,
  findType,
  results,
  sequence,
  onSelectPrimer,
  onClickEdit,
}: FindPrimersResultsProps): JSX.Element {
  const [resultSortPreference, setResultSortPreference] = usePreference<ResultSort>(
    FindPrimersPreferences.RESULTS_SORT
  );
  const checkboxRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (checkboxRef.current != null && !checkboxRef.current.classList.contains('select-all')) {
      checkboxRef.current.classList.add('select-all');
    }
  });

  const showName = [FindPrimersOptions.FOLDERS, FindPrimersOptions.WORKLIST].includes(findType);
  let resultSort: ResultSort;
  // We only show result names in folders and worklist search modes. If we're in bases or single-primer search
  // modes, start with location, but don't set the preference until the next explicit menu update
  if (resultSortPreference === RESULT_SORTS.NAME && !showName) {
    resultSort = RESULT_SORTS.LOCATION;
  } else {
    resultSort = resultSortPreference;
  }

  const sortedResults = sortResults(results, resultSort, sequence);

  const sequencePrimers = sequence.get('_sequence').sequencePrimers();
  // We have to check checked/disabled status in a loop in multiple places, sometimes with inverted logic.
  // Build a list, parallel to sortedResults, storing the checked/disabled status of the member of
  // sortedResults at the same index.
  const shouldBeDisabled = sortedResults.map(
    (sortedResult) => sortedResult.get('disabled') ?? !sortedResult.isNew()
  );
  const shouldBeChecked = sortedResults.map((sortedResult, index) => {
    if (shouldBeDisabled[index]) {
      return false;
    }
    const found = sequencePrimers.find((sequencePrimer) => sequencePrimer.cid === sortedResult.cid);
    return found != null && found.isNew();
  });
  const selectAllChecked = shouldBeChecked.some((r) => r);
  const selectAllIndeterminate = selectAllChecked && !shouldBeChecked.every((r) => r);
  const selectAllDisabled = shouldBeDisabled.every((r) => r);

  return (
    <>
      <div className={css(styles.header)}>
        <h3>Primers Search Results</h3>
        <SelectDropdown
          displayName={
            <span>
              <span className={css(styles.faded)}>Sort by </span>
              <span>{RESULT_SORT_TO_DISPLAY_NAME[resultSort]}</span>
            </span>
          }
          menu={
            <Menu value={resultSort}>
              {Object.entries(getResultSortOptionsAndDisplayNames(findType)).map(
                ([resultSortType, resultSortDisplay]: [
                  resultSortType: ResultSort | typeof RESULT_SORT_STRAND_TOGGLE,
                  resultSortDisplay: string,
                ]) => (
                  <Option
                    key={resultSortType}
                    value={resultSortType}
                    onSelect={(value: ResultSort | typeof RESULT_SORT_STRAND_TOGGLE) => {
                      let newResultSort: ResultSort;
                      if (value === RESULT_SORT_STRAND_TOGGLE) {
                        if (resultSort === RESULT_SORTS.STRAND_PLUS) {
                          newResultSort = RESULT_SORTS.STRAND_MINUS;
                        } else {
                          newResultSort = RESULT_SORTS.STRAND_PLUS;
                        }
                      } else {
                        newResultSort = value;
                      }
                      setResultSortPreference(newResultSort);
                    }}
                  >
                    {resultSortDisplay}
                  </Option>
                )
              )}
            </Menu>
          }
        />
      </div>
      <table className={classNames('table', 'binding-sites', {'no-highlight-matches': !highlightMatches})}>
        <colgroup>
          <col />
          {showName && <col className="name" />}
          <col className="location" />
          <col className="tm" />
          <col />
          <col className="edit" />
        </colgroup>
        <thead>
          <tr>
            <th>
              <CheckboxInput
                checked={selectAllChecked}
                disabled={selectAllDisabled}
                indeterminate={selectAllIndeterminate}
                label=""
                labelStyle={LabelStyle.HIDDEN}
                id="find-primers-results-select-all"
                inputRef={checkboxRef}
                onChange={() => {
                  if (!selectAllDisabled) {
                    if (!selectAllChecked) {
                      const primersToAdd = sortedResults.filter(
                        (sortedResult, index) =>
                          !shouldBeChecked[index] &&
                          !shouldBeDisabled[index] &&
                          !sequencePrimers.contains(sortedResult)
                      );
                      sequencePrimers.batchModify(() => {
                        sequencePrimers.add(primersToAdd);
                      });
                    } else {
                      const primersToRemove = sortedResults.filter(
                        (sortedResult, index) =>
                          shouldBeChecked[index] &&
                          !shouldBeDisabled[index] &&
                          sequencePrimers.contains(sortedResult)
                      );
                      sequencePrimers.batchModify(() => {
                        sequencePrimers.remove(primersToRemove);
                      });
                    }
                    onSelectPrimer();
                  }
                }}
              />
            </th>
            {showName && <th>Primer</th>}
            <th>Nucleotide Type</th>
            <th>Position</th>
            <th>{MELTING_TEMP_ABBREVIATION}</th>
            <th>
              Bases
              <span className={css(styles.helpIconWrapper)}>
                <Tooltip
                  placement={FloatingPlacement.RIGHT}
                  text={
                    `Select "Highlight matching bases" at the top of this tab to mark bases matching the ` +
                    'corresponding sequence in green.'
                  }
                >
                  <HelpIcon />
                </Tooltip>
              </span>
            </th>
            <th />
          </tr>
        </thead>
        <tbody {...getTestProps({component: 'FindPrimersResults', element: 'resultsTable'})}>
          {sortedResults.map((result, index) => {
            const disabled = shouldBeDisabled[index];
            const checked = shouldBeChecked[index];

            return (
              <FindPrimersRow
                bases={result.basesHtml(sequence)}
                checked={checked}
                disabled={disabled}
                end={result.end()}
                key={index}
                meltingTemperature={`${result.meltingTemperature().toFixed(1)}${DEGREE_SIGN}C`}
                nucleotideType={result.nucleotideType()}
                name={result.name()}
                onChange={(newChecked) => {
                  if (newChecked) {
                    sequencePrimers.add(result);
                  } else {
                    sequencePrimers.remove(result);
                  }
                  onSelectPrimer();
                }}
                onClick={() => {
                  const selection = sequence.get('selection');
                  selection.setEndpoints(result.start(), result.end());
                  sequence.trigger('jump', result.start());
                }}
                onClickEdit={() => {
                  onClickEdit(result);
                }}
                primerApiId={result.get('primer_api_id') ?? null}
                seqLength={sequence.length()}
                showName={showName}
                start={result.start()}
                startIndex={sequence.startIndex()}
                strand={result.strand()}
              />
            );
          })}
        </tbody>
      </table>
    </>
  );
}

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
  faded: {
    color: GRAY_DARK,
  },
  helpIconWrapper: {
    marginLeft: '5px',
  },
});
